import { useEffect, useState, type ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { effectiveTimezone } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';

/**
 * Locally declared so the module stays decoupled from the interface-language
 * context, matching `CommentTranslate` (`lib/comments.ts:37-41`). It is the
 * first parameter of `formatRelative` because that is the shipped house idiom
 * (`commentPermissionMessage(t, …)`) and because a required leading translator
 * makes a missed call site fail `tsc` rather than render English forever.
 */
type TimeTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

/**
 * `FE-04.10` primitive — resolved in `docs/design/stage2/B3-BRIEF.md` §4.
 *
 * The prop is `value`, not card-back's proposed `iso` — that name contradicts
 * the very finding that motivates this component existing at all: the string
 * every timestamp column in `packages/shared` actually carries is **not**
 * ISO 8601. It is Postgres' own spelling, e.g. `2026-08-10 12:00:00.123+00` —
 * a space rather than a `T`, and a two-digit UTC offset with no colon.
 * `new Date(...)` happens to parse that in V8 but is not spec-guaranteed
 * (ECMA-262 only mandates parsing the extended ISO format), and the failure
 * mode is a silent `Invalid Date` in, e.g., a notification drawer row. This
 * is the one place in the product that parses it explicitly — see
 * `parsePostgresTimestamp` below.
 */
const PG_TIMESTAMP_RE =
  /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})[ T](?<hour>\d{2}):(?<minute>\d{2}):(?<second>\d{2})(?:\.(?<fraction>\d+))?(?<offset>Z|[+-]\d{2}(?::?\d{2})?)?$/;

/**
 * Parses Postgres' `timestamptz` text representation (space-separated,
 * offset with no mandatory colon, e.g. `2026-08-10 12:00:00.123+00`) into a
 * `Date`. Exported so the parsing itself — the trap this component exists to
 * fix — has direct unit test coverage, not just coverage through rendering.
 *
 * Returns `null` on anything that does not match, rather than handing back
 * an `Invalid Date` for a caller to forget to check.
 */
export function parsePostgresTimestamp(value: string): Date | null {
  const match = PG_TIMESTAMP_RE.exec(value.trim());
  if (!match?.groups) {
    return null;
  }
  const { year, month, day, hour, minute, second, fraction, offset } = match.groups;
  const milliseconds = fraction ? Math.round(Number(`0.${fraction}`) * 1000) : 0;

  let offsetMinutes = 0;
  if (offset && offset !== 'Z') {
    const sign = offset.startsWith('-') ? -1 : 1;
    const digits = offset.slice(1).replace(':', '');
    const offsetHours = Number(digits.slice(0, 2));
    const offsetMins = digits.length > 2 ? Number(digits.slice(2, 4)) : 0;
    offsetMinutes = sign * (offsetHours * 60 + offsetMins);
  }

  // A string with no offset at all (`offset` undefined) leaves
  // `offsetMinutes` at 0 — i.e. treated as UTC. Defensible because every
  // wire field this parses is a `timestamptz` column, which Postgres never
  // serialises without one; it is a silent shift only if a naive
  // (non-`timestamptz`) value is ever routed through this component.
  const utcMillis =
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      milliseconds,
    ) -
    offsetMinutes * 60_000;

  const date = new Date(utcMillis);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * `messageKey` is a whole literal string on every entry, never composed as
 * `` `primitives.relativeTime.short.${unit}` ``: a template-composed key is
 * invisible to the two-directional catalogue diff that is the only mechanical
 * guard against a key shipping with no consumer (166 of the catalogue's keys
 * are already undiffable for exactly that reason, and this slice adds none).
 */
const RELATIVE_UNITS = [
  {
    unit: 'year' as const,
    ms: 365 * 24 * 60 * 60 * 1000,
    messageKey: 'primitives.relativeTime.short.year',
  },
  {
    unit: 'month' as const,
    ms: 30 * 24 * 60 * 60 * 1000,
    messageKey: 'primitives.relativeTime.short.month',
  },
  {
    unit: 'week' as const,
    ms: 7 * 24 * 60 * 60 * 1000,
    messageKey: 'primitives.relativeTime.short.week',
  },
  {
    unit: 'day' as const,
    ms: 24 * 60 * 60 * 1000,
    messageKey: 'primitives.relativeTime.short.day',
  },
  {
    unit: 'hour' as const,
    ms: 60 * 60 * 1000,
    messageKey: 'primitives.relativeTime.short.hour',
  },
  {
    unit: 'minute' as const,
    ms: 60 * 1000,
    messageKey: 'primitives.relativeTime.short.minute',
  },
];
const RELATIVE_REFRESH_MS = 60_000;

/**
 * ⚠ Two locale axes, on purpose. The `short` branch — the default, therefore
 * every comment byline, activity row and notification row — resolves against
 * the **catalogue** through `t`; the `long` branch stays on
 * `Intl.RelativeTimeFormat` against the **preferences** locale, and so does
 * `formatAbsolute`. `FE-80.1` deliberately moves neither `Intl` argument:
 * `resolvedLocale` has exactly one possible value (`en-GB`), so "negotiating"
 * the two axes today would replace native month names and native relative
 * phrases with British English for every `fr`/`de`/`es`/`pt-BR`/`ja`
 * preference, with no catalogue in their language to be consistent with.
 * Recorded as residual 9, not fixed here.
 */
function formatRelative(
  t: TimeTranslate,
  date: Date,
  reference: Date,
  format: 'short' | 'long',
  locale: string,
): string {
  const diffMs = date.getTime() - reference.getTime();
  const absMs = Math.abs(diffMs);

  if (absMs < 60_000) {
    return t('primitives.relativeTime.justNow');
  }

  for (const { unit, ms, messageKey } of RELATIVE_UNITS) {
    if (absMs >= ms) {
      // Floors, never rounds: elapsed time is a floor. `Math.round` would
      // read "60m" at 59m40s and "2h ago" at 90 minutes — overstating by up
      // to half a unit at every boundary, which is exactly the class of
      // silent-wrong this component exists to avoid for the timestamp parse.
      const magnitude = Math.floor(absMs / ms);
      if (format === 'long') {
        const signedValue = diffMs < 0 ? -magnitude : magnitude;
        return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
          signedValue,
          unit,
        );
      }
      // `{count}` is resolved from `values`. `interfaceMessage`'s own `count`
      // is the THIRD argument and selects a plural form; these keys are
      // `kind: 'text'`, so passing it there would leave `{count}` unresolved
      // and THROW at render.
      return t(messageKey, { count: magnitude });
    }
  }

  return t('primitives.relativeTime.justNow');
}

function formatAbsolute(date: Date, locale: string, timezone: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(date);
}

export interface RelativeTimeProps extends Omit<
  ComponentPropsWithoutRef<'time'>,
  'dateTime' | 'title' | 'children'
> {
  /** Postgres' `timestamptz` text spelling — NOT ISO 8601. See
   * `parsePostgresTimestamp` above. */
  value: string;
  format?: 'short' | 'long';
  /** Reference instant for the relative calculation. Defaults to `new
   * Date()`; overridable so callers and tests get deterministic output. */
  now?: Date;
}

export function RelativeTime({
  value,
  format = 'short',
  now,
  className,
  ...rest
}: RelativeTimeProps) {
  const preferences = useLocalePreferencesValue();
  const { t } = useInterfaceLanguage();
  const [liveNow, setLiveNow] = useState(() => new Date());
  const parsed = parsePostgresTimestamp(value);

  useEffect(() => {
    if (now !== undefined) return undefined;
    const interval = window.setInterval(() => {
      setLiveNow(new Date());
    }, RELATIVE_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [now]);

  if (!parsed) {
    // A silent `Invalid Date` is exactly the bug this primitive exists to
    // prevent — render an explicit, honest placeholder instead of leaking one.
    return (
      <time
        aria-label={t('primitives.relativeTime.unknown')}
        className={cn('text-ink-muted', className)}
        {...rest}
      >
        —
      </time>
    );
  }

  const reference = now ?? liveNow;
  const relative = formatRelative(t, parsed, reference, format, preferences.language);
  const absolute = formatAbsolute(
    parsed,
    preferences.language,
    effectiveTimezone(preferences),
  );

  return (
    <time
      dateTime={parsed.toISOString()}
      // `title` stays BARE — the absolute instant and nothing else. Feeding
      // the `accessibleName` template in here too would still satisfy
      // `FR-CARD-034.spec.ts:112` (non-empty only) while changing the tooltip
      // every user sees from `10 August 2026 at 13:00` to
      // `3h, 10 August 2026 at 13:00`.
      title={absolute}
      // The brief's wording is "the absolute instant reaching the accessible
      // name AS WELL AS `title`" — containment, not replacement. Overwriting
      // the name with the absolute instant alone would mean a screen-reader
      // user never hears the relative label the sighted rendering shows. The
      // comma and the ordering are the catalogue's, not this file's, so a
      // translated catalogue can reorder or re-punctuate them.
      aria-label={t('primitives.relativeTime.accessibleName', { relative, absolute })}
      className={cn(className)}
      {...rest}
    >
      {relative}
    </time>
  );
}
